import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import {
  ScreenType,
  useScreenType,
  WMS_MOBILE_MAX_WIDTH,
} from "@redotech/react-util/screen";
import { StorageLocationTableRow } from "@redotech/redo-fulfillment-wms-common/schema/storage-location";
import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc-client/client";
import { StorageLocationId } from "@redotech/redo-warehouse-id";
import { toast } from "@redotech/redo-web/alert";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoRadioButton } from "@redotech/redo-web/arbiter-components/radio/redo-radio-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useState } from "react";
import { LotBadge } from "../shared/lot-badge";
import { StorageLocationItemInfo } from "./storage-location-item-cell";

type InventoryItem = StorageLocationTableRow["inventoryItems"][number];

type AdjustmentMode = "adjust" | "set";

export interface AdjustInventoryTarget {
  storageLocationId: StorageLocationId;
  binName: string;
  item: InventoryItem;
}

export const AdjustInventoryModal = memo(function AdjustInventoryModal({
  target,
  onClose,
  onSuccess,
  rpcClient,
}: {
  target: AdjustInventoryTarget | null;
  onClose: () => void;
  onSuccess: (result: {
    previousQuantity: number;
    newQuantity: number;
  }) => void;
  rpcClient: RedoOutboundLabelsRpcClient;
}) {
  const [mode, setMode] = useState<AdjustmentMode>("adjust");
  // Each mode keeps its own value: "adjust by" accepts negatives and "set to"
  // does not, so sharing one number would clamp the user's entry on switch.
  const [adjustBy, setAdjustBy] = useState<number | undefined>(undefined);
  const [setTo, setSetTo] = useState<number | undefined>(undefined);
  const [note, setNote] = useState("");
  const quantityInputSize =
    useScreenType(WMS_MOBILE_MAX_WIDTH) === ScreenType.MOBILE
      ? "regular"
      : "small";

  // Reopening for a different item shouldn't inherit the last item's entries.
  useEffect(() => {
    if (target) {
      setMode("adjust");
      setAdjustBy(undefined);
      setSetTo(undefined);
      setNote("");
    }
  }, [target]);

  const handleClose = useHandler(() => {
    onClose();
  });

  const [saveLoad, triggerSave] = useTriggerLoad(async () => {
    if (!target || amount === undefined) {
      return;
    }
    try {
      const { previousQuantity, newQuantity } =
        await rpcClient.adjustInventoryLevel({
          storageLocationId: target.storageLocationId,
          productId: target.item.productInfo?.id,
          inventoryItemId: target.item.inventoryItemId,
          mode,
          amount,
          note: note.trim() || undefined,
        });
      toast(
        `Adjusted ${target.binName} from ${previousQuantity} to ${newQuantity}.`,
        { variant: "success" },
      );
      onSuccess({ previousQuantity, newQuantity });
      onClose();
    } catch (error) {
      toast(
        error instanceof Error && error.message
          ? `Failed to adjust inventory: ${error.message}`
          : "Failed to adjust inventory. Please try again.",
        { variant: "error" },
      );
    }
  });

  const currentQuantity = target?.item.quantity ?? 0;
  const amount = mode === "adjust" ? adjustBy : setTo;
  // With no amount entered yet the preview shows no change rather than a zero.
  const resultingQuantity =
    amount === undefined
      ? currentQuantity
      : mode === "set"
        ? amount
        : Math.max(0, currentQuantity + amount);

  return (
    <RedoModal
      isOpen={!!target}
      onModalCloseRequested={handleClose}
      primaryButton={{
        text: "Save",
        onClick: () => triggerSave(),
        disabled: amount === undefined || resultingQuantity === currentQuantity,
        pending: saveLoad.pending,
        size: "sm",
      }}
      secondaryButton={{ text: "Cancel", onClick: handleClose, size: "sm" }}
      subtitle={target ? `Bin ${target.binName}` : undefined}
      title="Adjust inventory"
    >
      <Flex dir="column" gap="xl">
        {target && (
          <Flex dir="column" gap="sm">
            <StorageLocationItemInfo item={target.item} showQuantity />
            {target.item.lotNumber && (
              <Flex align="center" gap="xs">
                <Text fontSize="xs" textColor="secondary">
                  Lot
                </Text>
                <LotBadge
                  expirationDate={target.item.expirationDate}
                  lotNumber={target.item.lotNumber}
                />
              </Flex>
            )}
          </Flex>
        )}

        <Flex align="flex-start" dir="column" gap="md">
          <RedoRadioButton
            label={
              <Flex dir="column" gap="xs">
                <Text fontSize="sm" fontWeight="medium">
                  Adjust by...
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  Change the current quantity by a given amount. Use a negative
                  number to decrease it.
                </Text>
              </Flex>
            }
            onSelect={() => setMode("adjust")}
            selected={mode === "adjust"}
            size="sm"
          />
          {mode === "adjust" && (
            <Flex style={{ marginLeft: "28px" }}>
              <RedoIncrementDecrement
                setValue={setAdjustBy}
                size={quantityInputSize}
                value={adjustBy}
                widthHint="1000"
              />
            </Flex>
          )}
        </Flex>

        <Flex align="flex-start" dir="column" gap="md">
          <RedoRadioButton
            label={
              <Flex dir="column" gap="xs">
                <Text fontSize="sm" fontWeight="medium">
                  Set to...
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  Set the quantity in this bin to a specific amount.
                </Text>
              </Flex>
            }
            onSelect={() => setMode("set")}
            selected={mode === "set"}
            size="sm"
          />
          {mode === "set" && (
            <Flex style={{ marginLeft: "28px" }}>
              <RedoIncrementDecrement
                min={0}
                setValue={setSetTo}
                size={quantityInputSize}
                value={setTo}
                widthHint="1000"
              />
            </Flex>
          )}
        </Flex>

        <Flex align="center" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            {`${currentQuantity} →`}
          </Text>
          <Text
            fontSize="xs"
            fontWeight="medium"
            textColor={
              resultingQuantity > currentQuantity
                ? "success"
                : resultingQuantity < currentQuantity
                  ? "error"
                  : "secondary"
            }
          >
            {resultingQuantity}
          </Text>
        </Flex>

        <Flex dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="medium">
            Note (optional)
          </Text>
          <RedoTextInput
            placeholder="Add a note..."
            setValue={setNote}
            size="sm"
            value={note}
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});
